import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchAll, type SearchResult } from "@/lib/content";

interface SearchBoxProps {
  size?: "md" | "lg";
  placeholder?: string;
  defaultValue?: string;
}

export default function SearchBox({ size = "md", placeholder = "搜索影视作品或角色名", defaultValue = "" }: SearchBoxProps) {
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const keyword = value.trim();
    if (!keyword) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      setResults(searchAll(keyword).slice(0, 6));
    }, 150);
    return () => window.clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const submit = () => {
    const keyword = value.trim();
    if (!keyword) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
  };

  const goResult = (result: SearchResult) => {
    setOpen(false);
    if (result.kind === "work") {
      navigate(`/detail/${result.id}`);
    } else {
      navigate(`/character/${result.id}`);
    }
  };

  const isLarge = size === "lg";

  return (
    <div ref={boxRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 rounded-full border border-background-300/70 bg-background-50 pl-4 pr-1.5 transition-shadow focus-within:border-primary-400 ${
          isLarge ? "h-14" : "h-11"
        }`}
      >
        <i className="ri-search-line text-foreground-400" style={{ fontSize: isLarge ? 20 : 17 }}></i>
        <input
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
          placeholder={placeholder}
          className={`flex-1 bg-transparent text-foreground-900 placeholder:text-foreground-400 outline-none ${
            isLarge ? "text-base" : "text-sm"
          }`}
        />
        <button
          type="button"
          onClick={submit}
          className={`shrink-0 whitespace-nowrap rounded-full bg-primary-500 font-medium text-background-50 transition-colors hover:bg-primary-600 ${
            isLarge ? "h-11 px-6 text-base" : "h-8 px-4 text-sm"
          }`}
        >
          搜索
        </button>
      </div>

      {open && value.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-background-200 bg-background-50 py-2 shadow-lg">
          {results.length === 0 ? (
            <p className="px-4 py-3 text-sm text-foreground-500">未找到与“{value.trim()}”相关的内容</p>
          ) : (
            results.map((result) => (
              <button
                key={`${result.kind}-${result.id}`}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => goResult(result)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
                    result.kind === "work" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
                  }`}
                >
                  <i className={result.kind === "work" ? "ri-film-line" : "ri-user-star-line"}></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-900">{result.title}</span>
                  <span className="block truncate text-xs text-foreground-500">{result.subtitle}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}